import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { MoreVertical, Plus, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import DraggableCard from "./DraggableCard";

export default function BoardColumn({ list, cards, onAddCard, onDeleteList, onCardClick }) {
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardTitle, setCardTitle] = useState("");

  const { setNodeRef } = useDroppable({
    id: `list-${list.id}`,
  });

  const handleAddCard = () => {
    if (cardTitle.trim()) {
      onAddCard(list.id, cardTitle);
      setCardTitle("");
      setShowAddCard(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className="min-w-[320px] max-w-[320px] bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 flex flex-col max-h-[calc(100vh-200px)]"
      data-testid={`list-${list.id}`}
    >
      {/* List Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex-1" data-testid={`list-title-${list.id}`}>{list.title}</h3>
        <AlertDialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`list-menu-${list.id}`}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <AlertDialogTrigger asChild>
                <DropdownMenuItem className="text-red-600" data-testid={`delete-list-trigger-${list.id}`}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Liste löschen
                </DropdownMenuItem>
              </AlertDialogTrigger>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Liste löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchtest Du die Liste "{list.title}" wirklich löschen? Alle Karten in dieser Liste werden ebenfalls gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDeleteList(list.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {cards.map((card) => (
            <DraggableCard key={card.id} card={card} onClick={() => onCardClick(card)} />
          ))}
        </SortableContext>
      </div>

      {/* Add Card */}
      <div className="p-4 border-t border-gray-200">
        {showAddCard ? (
          <div className="space-y-2">
            <Input
              data-testid={`new-card-input-${list.id}`}
              placeholder="Kartentitel eingeben..."
              value={cardTitle}
              onChange={(e) => setCardTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddCard()}
              autoFocus
            />
            <div className="flex gap-2">
              <Button data-testid={`add-card-button-${list.id}`} onClick={handleAddCard} size="sm" className="flex-1">
                Hinzufügen
              </Button>
              <Button
                onClick={() => {
                  setShowAddCard(false);
                  setCardTitle("");
                }}
                variant="outline"
                size="sm"
              >
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <Button
            data-testid={`show-add-card-${list.id}`}
            onClick={() => setShowAddCard(true)}
            variant="ghost"
            className="w-full justify-start text-gray-600 hover:bg-gray-100"
          >
            <Plus className="w-4 h-4 mr-2" />
            Karte hinzufügen
          </Button>
        )}
      </div>
    </div>
  );
}
